import { Folder, FormVersionStatus } from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import { EntrataSession } from 'auth/EntrataSession';

import { QueryKey } from 'hooks/queryKey';

import { getFolders } from '../../services/folders';
import { getApplications } from '../../services/index';

export enum ItemType {
  Application = 'application',
  FolderType = 'folder',
}

export type ListItem = {
  id: string;
  name: string;
  type: ItemType;
  folder?: Folder;
  application?: any; // Full application object from search results
};

export const useApplications = (folderId?: string) => {
  return useQuery({
    queryKey: [QueryKey.UseApplications, folderId || 'root'],
    queryFn: () => getApplications(EntrataSession?.user?.clientId, folderId),
  });
};

export const useFolderApplications = (folderId: string) => {
  return useQuery({
    queryKey: [QueryKey.UseApplications, 'folder', folderId],
    queryFn: () => getApplications(EntrataSession?.user?.clientId, folderId),
    enabled: !!folderId,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });
};

// Helper function to get the most recent published date from an application
const getMostRecentPublishedDate = (application?: any): Date | null => {
  if (!application?.versions) return null;

  const publishedVersions = application.versions.filter(
    (v: any) => v.status === FormVersionStatus.Active,
  );
  if (publishedVersions.length === 0) return null;

  // Find the most recently published version
  const sortedVersions = publishedVersions.sort(
    (a: any, b: any) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime(),
  );

  return new Date(sortedVersions[0].publishedDate);
};

export const useApplicationsAndFolders = (parentFolderId?: string) => {
  return useQuery({
    queryKey: [QueryKey.UseApplications, 'with-folders', parentFolderId || 'root'],
    queryFn: async () => {
      const [applications, folders] = await Promise.all([
        getApplications(EntrataSession?.user?.clientId, parentFolderId),
        getFolders(EntrataSession?.user?.clientId, parentFolderId),
      ]);

      const combined: ListItem[] = [
        ...folders.data.map((folder) => ({
          id: folder.id!,
          name: folder.name,
          type: ItemType.FolderType,
          folder,
        })),
        ...applications.data.map((app) => ({
          id: app.id,
          name: app.name,
          type: ItemType.Application,
          application: app,
        })),
      ];

      // Sort folders by sequence, then applications by most recent published date (newest first)
      return combined.sort((a, b) => {
        const aSeq = a.folder?.sequence ?? 0;
        const bSeq = b.folder?.sequence ?? 0;
        if (aSeq !== bSeq) return aSeq - bSeq;

        // For applications, find the most recent published date
        const aPublishedDate = getMostRecentPublishedDate(a.application);
        const bPublishedDate = getMostRecentPublishedDate(b.application);

        if (aPublishedDate && bPublishedDate) {
          return bPublishedDate.getTime() - aPublishedDate.getTime();
        }

        // If one has published date and other doesn't, prioritize the published one
        if (aPublishedDate && !bPublishedDate) return -1;
        if (!aPublishedDate && bPublishedDate) return 1;

        // Fallback to alphabetical sort
        return a.name.localeCompare(b.name);
      });
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });
};

export const useChildFolders = (parentFolderId: string) => {
  return useQuery({
    queryKey: [QueryKey.UseApplications, 'child-folders', parentFolderId],
    queryFn: async () => {
      const folders = await getFolders(EntrataSession?.user?.clientId, parentFolderId);
      return folders.data.map((folder) => ({
        id: folder.id!,
        name: folder.name,
        type: ItemType.FolderType,
        folder,
      }));
    },
    enabled: !!parentFolderId,
    staleTime: 1000 * 60 * 5,
  });
};
